import {
  DescMessage,
  JsonValue,
  MessageInitShape,
  MessageShape,
  create as protoCreate,
  fromJson as protoFromJson,
  toJson as protoToJson,
} from '@bufbuild/protobuf';

import {checkExists} from 'external/dev_april_corgi+/js/common/asserts';
import {
  Future,
  asFuture,
  resolvedFuture,
} from 'external/dev_april_corgi+/js/common/futures';
import {
  requestDataBatch as requestDataBatchUnsafe,
  getCache,
} from 'external/dev_april_corgi+/js/server/data';

import {BACKENDS, REQUEST_TYPES, RESPONSE_TYPES} from './backends';

export class SignedOutError extends Error {
  constructor(readonly cause: unknown) {
    super('User is signed out');
    this.name = this.constructor.name;
  }
}

// This whole stack converts BACKENDS into something like: {
//   'experiments.notion.NotionService/ListInventory:':
//       (request: ListInventoryRequest) => Promise<ListInventoryResponse>;
// }
type DataRequests = {
  [K in keyof typeof BACKENDS]: (typeof BACKENDS)[K]['methods'];
};
type AsMethods = {
  [K in keyof DataRequests]: {
    [L in keyof DataRequests[K] as `${K}/${Capitalize<string & L>}`]: DataRequests[K][L];
  };
};
type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (
  k: infer I,
) => void
  ? I
  : never;
type Merged = UnionToIntersection<AsMethods[keyof AsMethods]>;
type GetRequest<T> = T extends readonly [infer I extends DescMessage, any]
  ? MessageInitShape<I>
  : never;
type GetResponse<T> = T extends readonly [any, infer O extends DescMessage]
  ? MessageShape<O>
  : never;

interface Result<V> {
  kind: 'result';
  value: V;
}

interface Error {
  kind: 'error';
  code: number;
}

type RequestTuples<K extends keyof Merged> = Array<[K, GetRequest<Merged[K]>]>;
type ServerResponse = Result<object> | Error;
type ResponseBatch = Array<ServerResponse>;

let requestFuture: Future<ResponseBatch> | undefined;
let requestQueue: RequestTuples<keyof Merged> | undefined;

export function fetchData<
  K extends keyof Merged,
  Request extends GetRequest<Merged[K]>,
  Response extends GetResponse<Merged[K]>,
>(method: K, request: Request): Future<Response> {
  // We wait a tick to gather multiple keys before making the request. But if we're rendering on
  // the server we really just want to send it out now. Yolo.
  if (!process.env.CORGI_FOR_BROWSER) {
    return requestDataBatch([[method, request]]).then(
      r => unwrap(method, r[0]) as Response,
    );
  }

  const schema = checkExists(REQUEST_TYPES.get(method));
  const serialized = protoToJson(
    schema,
    protoCreate(schema, request),
  ) as object;
  const cached = getCache(method, serialized);
  if (cached) {
    return resolvedFuture(unwrap(method, cached as ServerResponse) as Response);
  }

  return requestData(method, request);
}

export function requestData<
  K extends keyof Merged,
  Request extends GetRequest<Merged[K]>,
  Response extends GetResponse<Merged[K]>,
>(method: K, request: Request): Future<Response> {
  if (!requestFuture || !requestQueue) {
    requestQueue = [];
    const captured = requestQueue;
    requestFuture = asFuture(Promise.resolve()).then(() => {
      requestFuture = undefined;
      requestQueue = undefined;
      return requestDataBatch(captured);
    });
  }

  const i = requestQueue.length;
  requestQueue.push([method, request]);
  return requestFuture.then(r => {
    const response = unwrap(method, r[i]) as Response;
    return response;
  });
}

function requestDataBatch(
  tuples: RequestTuples<keyof Merged>,
): Future<ResponseBatch> {
  const serialized = tuples.map(([k, v]) => {
    const schema = checkExists(REQUEST_TYPES.get(k));
    return [k, protoToJson(schema, protoCreate(schema, v))] as [string, object];
  });
  return requestDataBatchUnsafe(serialized) as Future<ResponseBatch>;
}

function unwrap<K extends keyof Merged, T extends GetResponse<Merged[K]>>(
  method: K,
  response: ServerResponse,
): T {
  if (response.kind === 'result') {
    const schema = checkExists(RESPONSE_TYPES.get(method));
    return protoFromJson(schema, response.value as JsonValue) as T;
  } else {
    throw new Error(`Request failed with code ${response.code}`);
  }
}
